/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import { db, callFunctionWithFallback } from '../firebase';
import { collection, getDocs, addDoc, deleteDoc, doc, serverTimestamp, updateDoc, setDoc } from 'firebase/firestore';
import { Trash2, Upload, FileText, ClipboardList, AlertCircle, CheckCircle2, Edit2, X, Building2, ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { useIndustries, IndustryData } from '../context/IndustryContext';
import { getUserFriendlyErrorMessage } from '../utils';

// ─── File Text Extraction Utilities ───────────────────────────────────────────

async function extractTextFromFile(file: File): Promise<string> {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';

    if (ext === 'txt') {
        return file.text();
    }

    if (ext === 'xlsx' || ext === 'xls') {
        const XLSX = await import('xlsx');
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const lines: string[] = [];
        workbook.SheetNames.forEach(sheetName => {
            lines.push(`=== Sheet: ${sheetName} ===`);
            const sheet = workbook.Sheets[sheetName];
            const csv = XLSX.utils.sheet_to_csv(sheet);
            lines.push(csv);
        });
        return lines.join('\n');
    }

    if (ext === 'docx' || ext === 'doc') {
        const mammoth = await import('mammoth');
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        return result.value;
    }

    if (ext === 'pdf') {
        // PDF binary text extraction — send raw for Gemini to interpret
        // Best effort: read as text; Gemini handles partially-structured PDF text well
        return file.text();
    }

    return file.text();
}

// ─── Knowledge Base Section ───────────────────────────────────────────────────

const KnowledgeBaseSection: React.FC = () => {
    const { industries: globalIndustries, scenarios: globalScenarios } = useIndustries();
    const [title, setTitle] = useState('');
    const [industry, setIndustry] = useState('');
    const [scenario, setScenario] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [knowledgeBases, setKnowledgeBases] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editingKb, setEditingKb] = useState<any | null>(null);
    const [editKbTitle, setEditKbTitle] = useState('');
    const [editKbIndustry, setEditKbIndustry] = useState('');
    const [editKbScenario, setEditKbScenario] = useState('');
    const [editKbText, setEditKbText] = useState('');

    const availableScenarios = industry ? globalScenarios[industry] || [] : [];

    const fetchKbs = async () => {
        try {
            const snap = await getDocs(collection(db, 'knowledgeBases'));
            const kbs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            kbs.sort((a: any, b: any) => {
                const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
                const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
                return timeB - timeA;
            });
            setKnowledgeBases(kbs);
        } catch (err) {
            console.error('Failed to fetch knowledge bases:', err);
            setError('Failed to fetch knowledge bases');
        }
    };

    useEffect(() => { fetchKbs(); }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!file) return;
        setError(null);
        setLoading(true);
        try {
            const text = await file.text();
            await addDoc(collection(db, 'knowledgeBases'), {
                title, industry, scenario, text,
                createdAt: serverTimestamp()
            });
            setTitle(''); setIndustry(''); setScenario(''); setFile(null);
            (document.getElementById('kbFileInput') as HTMLInputElement).value = '';
            await fetchKbs();
        } catch (err) {
            console.error('Failed to upload KB:', err);
            setError(getUserFriendlyErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this knowledge base?')) return;
        try {
            await deleteDoc(doc(db, 'knowledgeBases', id));
            await fetchKbs();
        } catch (err) {
            console.error('Failed to delete KB:', err);
            setError('Failed to delete KB');
        }
    };

    const handleEditClick = (kb: any) => {
        setEditingKb(kb);
        setEditKbTitle(kb.title || '');
        setEditKbIndustry(kb.industry || '');
        setEditKbScenario(kb.scenario || '');
        setEditKbText(kb.text || '');
    };

    const handleSaveEdit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingKb) return;
        setLoading(true);
        try {
            await updateDoc(doc(db, 'knowledgeBases', editingKb.id), {
                title: editKbTitle,
                industry: editKbIndustry,
                scenario: editKbScenario,
                text: editKbText
            });
            setEditingKb(null);
            await fetchKbs();
        } catch (err) {
            console.error('Failed to update KB:', err);
            setError('Failed to update knowledge base');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={{ background: 'white', borderRadius: '1rem', border: '1px solid #F1F3F5', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1F2937', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Upload size={18} /> Upload New Knowledge Base
                </h3>
                {error && <div className="fb-point fb-imp" style={{ marginBottom: '1.5rem' }}><p>{error}</p></div>}
                <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem', alignItems: 'end' }}>
                    <div className="form-group-fancy" style={{ margin: 0 }}>
                        <label>Title</label>
                        <input type="text" className="input-fancy" required value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Billing Dispute v2" />
                    </div>
                    <div className="form-group-fancy" style={{ margin: 0 }}>
                        <label>Industry</label>
                        <select className="input-fancy" required value={industry} onChange={e => { setIndustry(e.target.value); setScenario(''); }}>
                            <option value="">Select Industry</option>
                            {globalIndustries.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                        </select>
                    </div>
                    <div className="form-group-fancy" style={{ margin: 0 }}>
                        <label>Scenario</label>
                        <select className="input-fancy" required disabled={!industry} value={scenario} onChange={e => setScenario(e.target.value)}>
                            <option value="">{industry ? 'Select Scenario' : 'Select Industry First'}</option>
                            {availableScenarios.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div className="form-group-fancy" style={{ margin: 0 }}>
                        <label>Knowledge Base (.txt)</label>
                        <input type="file" id="kbFileInput" className="input-fancy" accept=".txt" required onChange={e => setFile(e.target.files ? e.target.files[0] : null)} />
                    </div>
                    <button type="submit" className="button button-primary" disabled={loading} style={{ padding: '0.65rem 1rem' }}>
                        {loading ? 'Uploading...' : 'Upload KB'}
                    </button>
                </form>
            </div>
            <div style={{ background: 'white', borderRadius: '1rem', border: '1px solid #F1F3F5', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1F2937', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FileText size={18} /> Manage Knowledge Bases
                </h3>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid #E5E7EB', color: '#6B7280', textAlign: 'left' }}>
                                <th style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>Title</th>
                                <th style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>Industry</th>
                                <th style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>Scenario</th>
                                <th style={{ padding: '0.75rem 1rem', fontWeight: 600, textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {knowledgeBases.length === 0 ? (
                                <tr><td colSpan={4} style={{ padding: '2rem', textAlign: 'center', color: '#9CA3AF' }}>No knowledge bases uploaded yet.</td></tr>
                            ) : (
                                knowledgeBases.map(kb => (
                                    <tr key={kb.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                                        <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#111827' }}>{kb.title}</td>
                                        <td style={{ padding: '0.75rem 1rem', color: '#4B5563' }}>{kb.industry}</td>
                                        <td style={{ padding: '0.75rem 1rem', color: '#4B5563' }}>{kb.scenario}</td>
                                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                                            <button onClick={() => handleEditClick(kb)} style={{ background: 'transparent', border: 'none', color: '#4F46E5', cursor: 'pointer', padding: '0.25rem', marginRight: '0.5rem' }} title="Edit">
                                                <Edit2 size={16} />
                                            </button>
                                            <button onClick={() => handleDelete(kb.id)} style={{ background: 'transparent', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '0.25rem' }} title="Delete">
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Edit KB Modal */}
            {editingKb && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'white', borderRadius: '1rem', padding: '2rem', width: '90%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Edit Knowledge Base</h3>
                            <button onClick={() => setEditingKb(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6B7280' }}>
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleSaveEdit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            <div className="form-group-fancy" style={{ margin: 0 }}>
                                <label>Title</label>
                                <input type="text" className="input-fancy" required value={editKbTitle} onChange={e => setEditKbTitle(e.target.value)} />
                            </div>
                            <div className="form-group-fancy" style={{ margin: 0 }}>
                                <label>Industry</label>
                                <select className="input-fancy" required value={editKbIndustry} onChange={e => { setEditKbIndustry(e.target.value); setEditKbScenario(''); }}>
                                    <option value="">Select Industry</option>
                                    {globalIndustries.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                                </select>
                            </div>
                            <div className="form-group-fancy" style={{ margin: 0 }}>
                                <label>Scenario</label>
                                <select className="input-fancy" required disabled={!editKbIndustry} value={editKbScenario} onChange={e => setEditKbScenario(e.target.value)}>
                                    <option value="">{editKbIndustry ? 'Select Scenario' : 'Select Industry First'}</option>
                                    {(editKbIndustry ? globalScenarios[editKbIndustry] || [] : []).map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div className="form-group-fancy" style={{ margin: 0 }}>
                                <label>Knowledge Base Content</label>
                                <textarea className="input-fancy" rows={10} required value={editKbText} onChange={e => setEditKbText(e.target.value)} style={{ resize: 'vertical', fontFamily: 'inherit' }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
                                <button type="button" onClick={() => setEditingKb(null)} className="button" style={{ background: '#F3F4F6', color: '#374151' }}>Cancel</button>
                                <button type="submit" className="button button-primary" disabled={loading}>{loading ? 'Saving...' : 'Save Changes'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Assessment Forms Section ─────────────────────────────────────────────────

const AssessmentFormsSection: React.FC = () => {
    const [title, setTitle] = useState('');
    const [additionalInstructions, setAdditionalInstructions] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [rubrics, setRubrics] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);
    const [editingRubric, setEditingRubric] = useState<any | null>(null);
    const [editTitle, setEditTitle] = useState('');
    const [editInstructions, setEditInstructions] = useState('');
    const [editDescription, setEditDescription] = useState('');

    const fetchRubrics = async () => {
        try {
            const snap = await getDocs(collection(db, 'assessmentRubrics'));
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            list.sort((a: any, b: any) => {
                const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
                const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
                return timeB - timeA;
            });
            setRubrics(list);
        } catch (err) {
            console.error('Failed to fetch rubrics:', err);
        }
    };

    useEffect(() => { fetchRubrics(); }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!file) return;
        setError(null);
        setSuccessMsg(null);
        setLoading(true);

        try {
            const ext = file.name.split('.').pop()?.toLowerCase() || 'txt';
            const fileText = await extractTextFromFile(file);

            if (!fileText || fileText.trim().length < 20) {
                throw new Error('Could not extract meaningful text from this file. Please try a .txt or .docx format.');
            }

            const result: any = await callFunctionWithFallback('parseRubricFile', {
                title,
                fileText,
                additionalInstructions,
                fileType: ext,
            });

            const { categoryCount, description } = result.data;
            setSuccessMsg(`✓ "${title}" uploaded successfully — ${categoryCount} categories detected. ${description || ''}`);
            setTitle('');
            setAdditionalInstructions('');
            setFile(null);
            (document.getElementById('rubricFileInput') as HTMLInputElement).value = '';
            await fetchRubrics();
        } catch (err: any) {
            console.error('Failed to upload rubric:', err);
            setError(getUserFriendlyErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this assessment form?')) return;
        try {
            await deleteDoc(doc(db, 'assessmentRubrics', id));
            await fetchRubrics();
        } catch (err) {
            console.error('Failed to delete rubric:', err);
            setError('Failed to delete assessment form.');
        }
    };

    const handleEditClick = (rubric: any) => {
        setEditingRubric(rubric);
        setEditTitle(rubric.title || '');
        setEditDescription(rubric.description || '');
        setEditInstructions(rubric.additionalInstructions || '');
    };

    const handleSaveEdit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingRubric) return;
        setLoading(true);
        try {
            await updateDoc(doc(db, 'assessmentRubrics', editingRubric.id), {
                title: editTitle,
                description: editDescription,
                additionalInstructions: editInstructions,
            });
            setSuccessMsg(`✓ Form "${editTitle}" updated successfully.`);
            setEditingRubric(null);
            await fetchRubrics();
        } catch (err) {
            console.error('Failed to update rubric:', err);
            setError('Failed to update assessment form.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* Info Banner */}
            <div style={{ background: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '0.75rem', padding: '1rem 1.25rem', fontSize: '0.85rem', color: '#4338CA', lineHeight: 1.6 }}>
                <strong>How it works:</strong> Upload your rubric in any format (Excel, Word, PDF, TXT). Gemini will analyze and extract the scoring categories, weights, and rules. The AI will then <strong>strictly follow</strong> the selected form when assessing interactions — no blending with other criteria.
                <br /><br />
                <strong>Tip:</strong> For best parsing results, use <strong>.txt</strong> or <strong>.docx</strong>. Excel files work well for structured rubrics. PDF support is best-effort.
            </div>

            {/* Upload Form */}
            <div style={{ background: 'white', borderRadius: '1rem', border: '1px solid #F1F3F5', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1F2937', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Upload size={18} /> Upload New Assessment Form
                </h3>

                {error && <div className="fb-point fb-imp" style={{ marginBottom: '1rem' }}><AlertCircle size={16} /><p>{error}</p></div>}
                {successMsg && (
                    <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#065F46', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                        <CheckCircle2 size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                        <span>{successMsg}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem', alignItems: 'end' }}>
                        <div className="form-group-fancy" style={{ margin: 0 }}>
                            <label>Form Title</label>
                            <input
                                type="text"
                                className="input-fancy"
                                required
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                placeholder="e.g. Sales QA Form v2, CSAT Rubric"
                            />
                        </div>
                        <div className="form-group-fancy" style={{ margin: 0 }}>
                            <label>Upload File (.txt, .xlsx, .xls, .docx, .doc, .pdf)</label>
                            <input
                                type="file"
                                id="rubricFileInput"
                                className="input-fancy"
                                accept=".txt,.xlsx,.xls,.docx,.doc,.pdf"
                                required
                                onChange={e => setFile(e.target.files ? e.target.files[0] : null)}
                            />
                        </div>
                    </div>
                    <div className="form-group-fancy" style={{ margin: 0 }}>
                        <label>Additional AI Instructions <span style={{ fontWeight: 400, color: '#6B7280' }}>(Optional — adds guidance not already in the file)</span></label>
                        <textarea
                            className="input-fancy"
                            rows={3}
                            value={additionalInstructions}
                            onChange={e => setAdditionalInstructions(e.target.value)}
                            placeholder="e.g. Treat the Greeting category as always mandatory. Double-weight empathy for this team. Deduct 2 points if the agent does not use the customer's name."
                            style={{ resize: 'vertical', fontFamily: 'inherit' }}
                        />
                    </div>
                    <div>
                        <button type="submit" className="button button-primary" disabled={loading} style={{ padding: '0.65rem 1.5rem' }}>
                            {loading ? '🔄 Analyzing & Uploading...' : '⬆ Upload & Parse Form'}
                        </button>
                        {loading && (
                            <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#6B7280' }}>
                                Gemini is analyzing your rubric. This may take 15–30 seconds...
                            </p>
                        )}
                    </div>
                </form>
            </div>

            {/* Manage Rubrics Table */}
            <div style={{ background: 'white', borderRadius: '1rem', border: '1px solid #F1F3F5', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1F2937', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <ClipboardList size={18} /> Manage Assessment Forms
                </h3>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid #E5E7EB', color: '#6B7280', textAlign: 'left' }}>
                                <th style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>Form Title</th>
                                <th style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>Description</th>
                                <th style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>Categories</th>
                                <th style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>Type</th>
                                <th style={{ padding: '0.75rem 1rem', fontWeight: 600, textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Built-in Generic Form — always shown, non-deletable */}
                            <tr style={{ borderBottom: '1px solid #F3F4F6', background: '#F8FAFF' }}>
                                <td style={{ padding: '0.75rem 1rem', fontWeight: 600, color: '#4338CA' }}>
                                    Generic Form
                                    <span style={{ marginLeft: '0.5rem', fontSize: '0.72rem', background: 'rgba(99,102,241,0.12)', color: '#6366F1', borderRadius: '999px', padding: '0.15rem 0.5rem', fontWeight: 600 }}>BUILT-IN</span>
                                </td>
                                <td style={{ padding: '0.75rem 1rem', color: '#6B7280', fontSize: '0.8rem' }}>Standard 8-category customer service quality evaluation</td>
                                <td style={{ padding: '0.75rem 1rem', color: '#4B5563' }}>8</td>
                                <td style={{ padding: '0.75rem 1rem', color: '#4B5563' }}>Built-in</td>
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#9CA3AF', fontSize: '0.8rem' }}>—</td>
                            </tr>
                            {rubrics.length === 0 ? (
                                <tr><td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: '#9CA3AF' }}>No custom assessment forms uploaded yet.</td></tr>
                            ) : (
                                rubrics.map(r => (
                                    <tr key={r.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                                        <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#111827' }}>{r.title}</td>
                                        <td style={{ padding: '0.75rem 1rem', color: '#6B7280', fontSize: '0.8rem', maxWidth: '280px' }}>{r.description || '—'}</td>
                                        <td style={{ padding: '0.75rem 1rem', color: '#4B5563' }}>{r.categoryCount || '—'}</td>
                                        <td style={{ padding: '0.75rem 1rem', color: '#4B5563', textTransform: 'uppercase', fontSize: '0.75rem' }}>{r.fileType || 'txt'}</td>
                                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                                            <button onClick={() => handleEditClick(r)} style={{ background: 'transparent', border: 'none', color: '#4F46E5', cursor: 'pointer', padding: '0.25rem', marginRight: '0.5rem' }} title="Edit">
                                                <Edit2 size={16} />
                                            </button>
                                            <button onClick={() => handleDelete(r.id)} style={{ background: 'transparent', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '0.25rem' }} title="Delete">
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Edit Modal */}
            {editingRubric && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'white', borderRadius: '1rem', padding: '2rem', width: '90%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Edit Assessment Form</h3>
                            <button onClick={() => setEditingRubric(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6B7280' }}>
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleSaveEdit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            <div className="form-group-fancy" style={{ margin: 0 }}>
                                <label>Form Title</label>
                                <input type="text" className="input-fancy" required value={editTitle} onChange={e => setEditTitle(e.target.value)} />
                            </div>
                            <div className="form-group-fancy" style={{ margin: 0 }}>
                                <label>Description (1-2 sentences)</label>
                                <textarea className="input-fancy" rows={2} value={editDescription} onChange={e => setEditDescription(e.target.value)} />
                            </div>
                            <div className="form-group-fancy" style={{ margin: 0 }}>
                                <label>Additional AI Instructions</label>
                                <textarea className="input-fancy" rows={4} value={editInstructions} onChange={e => setEditInstructions(e.target.value)} placeholder="e.g. Deduct 2 points if the agent does not use the customer's name." style={{ resize: 'vertical' }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
                                <button type="button" onClick={() => setEditingRubric(null)} className="button" style={{ background: '#F3F4F6', color: '#374151' }}>Cancel</button>
                                <button type="submit" className="button button-primary" disabled={loading}>{loading ? 'Saving...' : 'Save Changes'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Industry Management Section ──────────────────────────────────────────────

const IndustryManagementSection: React.FC = () => {
    const { industryData, loading, refreshIndustries } = useIndustries();
    const [newIndustryName, setNewIndustryName] = useState('');
    const [expandedIndustryId, setExpandedIndustryId] = useState<string | null>(null);
    const [newScenarioName, setNewScenarioName] = useState('');

    const [editingIndustryId, setEditingIndustryId] = useState<string | null>(null);
    const [editIndustryName, setEditIndustryName] = useState('');
    
    const [editingScenarioIndex, setEditingScenarioIndex] = useState<{ industryId: string, index: number } | null>(null);
    const [editScenarioName, setEditScenarioName] = useState('');
    
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleAddIndustry = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newIndustryName.trim()) return;
        setError(null);
        setActionLoading(true);
        try {
            const id = newIndustryName.toLowerCase().replace(/\s+/g, '-');
            const docRef = doc(db, 'industries', id);
            await setDoc(docRef, { name: newIndustryName.trim(), scenarios: [], createdAt: serverTimestamp() });
            setNewIndustryName('');
            await refreshIndustries();
        } catch (err) {
            console.error('Failed to add industry:', err);
            setError(getUserFriendlyErrorMessage(err));
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteIndustry = async (id: string, name: string) => {
        if (!window.confirm(`Are you sure you want to delete the "${name}" industry and all its scenarios?`)) return;
        setActionLoading(true);
        try {
            await deleteDoc(doc(db, 'industries', id));
            if (expandedIndustryId === id) setExpandedIndustryId(null);
            await refreshIndustries();
        } catch (err) {
            setError(getUserFriendlyErrorMessage(err));
        } finally {
            setActionLoading(false);
        }
    };

    const handleSaveIndustryName = async (id: string) => {
        if (!editIndustryName.trim()) return;
        setActionLoading(true);
        try {
            await updateDoc(doc(db, 'industries', id), { name: editIndustryName.trim() });
            setEditingIndustryId(null);
            await refreshIndustries();
        } catch (err) {
            setError(getUserFriendlyErrorMessage(err));
        } finally {
            setActionLoading(false);
        }
    };

    const handleAddScenario = async (e: React.FormEvent, ind: IndustryData) => {
        e.preventDefault();
        if (!newScenarioName.trim()) return;
        setActionLoading(true);
        try {
            const newScenarios = [...ind.scenarios, newScenarioName.trim()];
            await updateDoc(doc(db, 'industries', ind.id), { scenarios: newScenarios });
            setNewScenarioName('');
            await refreshIndustries();
        } catch (err) {
            setError(getUserFriendlyErrorMessage(err));
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteScenario = async (ind: IndustryData, index: number) => {
        if (!window.confirm('Delete this scenario?')) return;
        setActionLoading(true);
        try {
            const newScenarios = [...ind.scenarios];
            newScenarios.splice(index, 1);
            await updateDoc(doc(db, 'industries', ind.id), { scenarios: newScenarios });
            await refreshIndustries();
        } catch (err) {
            setError(getUserFriendlyErrorMessage(err));
        } finally {
            setActionLoading(false);
        }
    };

    const handleSaveScenario = async (ind: IndustryData, index: number) => {
        if (!editScenarioName.trim()) return;
        setActionLoading(true);
        try {
            const newScenarios = [...ind.scenarios];
            newScenarios[index] = editScenarioName.trim();
            await updateDoc(doc(db, 'industries', ind.id), { scenarios: newScenarios });
            setEditingScenarioIndex(null);
            await refreshIndustries();
        } catch (err) {
            setError(getUserFriendlyErrorMessage(err));
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#9CA3AF' }}>Loading...</div>;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={{ background: 'white', borderRadius: '1rem', border: '1px solid #F1F3F5', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1F2937', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Plus size={18} /> Add New Industry
                </h3>
                {error && <div className="fb-point fb-imp" style={{ marginBottom: '1.5rem' }}><p>{error}</p></div>}
                <form onSubmit={handleAddIndustry} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                    <div className="form-group-fancy" style={{ margin: 0, flexGrow: 1, maxWidth: '400px' }}>
                        <label>Industry Name</label>
                        <input type="text" className="input-fancy" required value={newIndustryName} onChange={e => setNewIndustryName(e.target.value)} placeholder="e.g. Real Estate" />
                    </div>
                    <button type="submit" className="button button-primary" disabled={actionLoading} style={{ padding: '0.65rem 1.5rem', height: 'fit-content' }}>
                        {actionLoading ? 'Adding...' : 'Add Industry'}
                    </button>
                </form>
            </div>

            <div style={{ background: 'white', borderRadius: '1rem', border: '1px solid #F1F3F5', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1F2937', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Building2 size={18} /> Manage Industries & Scenarios
                </h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {industryData.length === 0 ? (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#9CA3AF' }}>No industries configured.</div>
                    ) : (
                        industryData.map(ind => {
                            const isExpanded = expandedIndustryId === ind.id;
                            const isEditingTitle = editingIndustryId === ind.id;
                            return (
                                <div key={ind.id} style={{ border: '1px solid #E5E7EB', borderRadius: '0.75rem', overflow: 'hidden' }}>
                                    <div 
                                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', background: '#FAFAFA', borderBottom: isExpanded ? '1px solid #E5E7EB' : 'none' }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexGrow: 1 }}>
                                            <button onClick={() => setExpandedIndustryId(isExpanded ? null : ind.id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', color: '#6B7280' }}>
                                                {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                            </button>
                                            
                                            {isEditingTitle ? (
                                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                    <input 
                                                        type="text" 
                                                        className="input-fancy" 
                                                        style={{ padding: '0.3rem 0.5rem', fontSize: '0.9rem', margin: 0, minWidth: '200px' }} 
                                                        value={editIndustryName} 
                                                        onChange={e => setEditIndustryName(e.target.value)} 
                                                        autoFocus
                                                    />
                                                    <button onClick={() => handleSaveIndustryName(ind.id)} disabled={actionLoading} style={{ background: '#10B981', color: 'white', border: 'none', borderRadius: '0.25rem', padding: '0.3rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 }}>Save</button>
                                                    <button onClick={() => setEditingIndustryId(null)} disabled={actionLoading} style={{ background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '0.25rem', padding: '0.3rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer' }}>Cancel</button>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    <span style={{ fontWeight: 600, color: '#111827', fontSize: '0.95rem' }}>{ind.name}</span>
                                                    <span style={{ fontSize: '0.75rem', background: '#E5E7EB', color: '#4B5563', padding: '0.1rem 0.4rem', borderRadius: '1rem', fontWeight: 600 }}>{ind.scenarios.length} scenarios</span>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {!isEditingTitle && (
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <button onClick={() => { setEditingIndustryId(ind.id); setEditIndustryName(ind.name); }} style={{ background: 'transparent', border: 'none', color: '#4F46E5', cursor: 'pointer', padding: '0.25rem' }} title="Edit Industry Name">
                                                    <Edit2 size={16} />
                                                </button>
                                                <button onClick={() => handleDeleteIndustry(ind.id, ind.name)} style={{ background: 'transparent', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '0.25rem' }} title="Delete Industry">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {isExpanded && (
                                        <div style={{ padding: '1rem 1.25rem', background: 'white' }}>
                                            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem 0', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                {ind.scenarios.map((scenario, index) => {
                                                    const isEditing = editingScenarioIndex?.industryId === ind.id && editingScenarioIndex?.index === index;
                                                    return (
                                                        <li key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 1rem', background: '#F9FAFB', borderRadius: '0.5rem', border: '1px solid #F1F3F5' }}>
                                                            {isEditing ? (
                                                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexGrow: 1 }}>
                                                                    <input 
                                                                        type="text" 
                                                                        className="input-fancy" 
                                                                        style={{ padding: '0.3rem 0.5rem', fontSize: '0.85rem', margin: 0, flexGrow: 1, maxWidth: '300px' }} 
                                                                        value={editScenarioName} 
                                                                        onChange={e => setEditScenarioName(e.target.value)} 
                                                                        autoFocus
                                                                    />
                                                                    <button onClick={() => handleSaveScenario(ind, index)} disabled={actionLoading} style={{ background: '#10B981', color: 'white', border: 'none', borderRadius: '0.25rem', padding: '0.3rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 }}>Save</button>
                                                                    <button onClick={() => setEditingScenarioIndex(null)} disabled={actionLoading} style={{ background: '#E5E7EB', color: '#374151', border: 'none', borderRadius: '0.25rem', padding: '0.3rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer' }}>Cancel</button>
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <span style={{ fontSize: '0.875rem', color: '#374151' }}>{scenario}</span>
                                                                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                                                                        <button onClick={() => { setEditingScenarioIndex({ industryId: ind.id, index }); setEditScenarioName(scenario); }} style={{ background: 'transparent', border: 'none', color: '#6B7280', cursor: 'pointer', padding: '0.25rem' }} title="Edit Scenario">
                                                                            <Edit2 size={14} />
                                                                        </button>
                                                                        <button onClick={() => handleDeleteScenario(ind, index)} style={{ background: 'transparent', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '0.25rem' }} title="Delete Scenario">
                                                                            <Trash2 size={14} />
                                                                        </button>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                                {ind.scenarios.length === 0 && (
                                                    <li style={{ fontSize: '0.85rem', color: '#9CA3AF', padding: '0.5rem' }}>No scenarios added yet.</li>
                                                )}
                                            </ul>
                                            
                                            <form onSubmit={(e) => handleAddScenario(e, ind)} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                                <input 
                                                    type="text" 
                                                    className="input-fancy" 
                                                    style={{ padding: '0.45rem 0.75rem', fontSize: '0.85rem', margin: 0, flexGrow: 1, maxWidth: '250px' }} 
                                                    placeholder="New Scenario Name..." 
                                                    value={newScenarioName} 
                                                    onChange={e => setNewScenarioName(e.target.value)} 
                                                />
                                                <button type="submit" disabled={!newScenarioName.trim() || actionLoading} style={{ background: '#4F46E5', color: 'white', border: 'none', borderRadius: '0.35rem', padding: '0.45rem 0.85rem', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 }}>
                                                    Add Scenario
                                                </button>
                                            </form>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

// ─── Main Component with Tabs ─────────────────────────────────────────────────

type Tab = 'knowledge' | 'assessments' | 'industries';

export const AdminContentManagement: React.FC = () => {
    const [activeTab, setActiveTab] = useState<Tab>('knowledge');

    const tabStyle = (tab: Tab): React.CSSProperties => ({
        padding: '0.6rem 1.25rem',
        borderRadius: '0.5rem',
        border: 'none',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '0.875rem',
        transition: 'all 0.15s',
        background: activeTab === tab ? '#4F46E5' : 'transparent',
        color: activeTab === tab ? 'white' : '#6B7280',
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* Tab Bar */}
            <div style={{ display: 'flex', gap: '0.5rem', padding: '0.4rem', background: '#F3F4F6', borderRadius: '0.75rem', width: 'fit-content' }}>
                <button style={tabStyle('knowledge')} onClick={() => setActiveTab('knowledge')}>
                    📚 Knowledge Bases
                </button>
                <button style={tabStyle('assessments')} onClick={() => setActiveTab('assessments')}>
                    📋 Assessment Forms
                </button>
                <button style={tabStyle('industries')} onClick={() => setActiveTab('industries')}>
                    🏢 Industries & Scenarios
                </button>
            </div>

            {activeTab === 'knowledge' && <KnowledgeBaseSection />}
            {activeTab === 'assessments' && <AssessmentFormsSection />}
            {activeTab === 'industries' && <IndustryManagementSection />}
        </div>
    );
};
